// Masked-secret input. Two visual modes:
//
//   Display mode (the default when a secret is already set on the
//   server) — shows fingerprint + rotated-at; no <input> in the DOM.
//   The plaintext is never re-rendered, because the BE never sends it
//   back to the client after the initial save.
//
//   Edit mode (after Rotate is clicked, or unconditionally when no
//   secret is set yet) — an UNCONTROLLED password input wrapped in a
//   <form> with a companion hidden username field. The input is
//   uncontrolled by design: React never sets its `value` attribute, so
//   if Chrome ever decides to dump the element to the console (some
//   DOM-violation paths do) the attribute string is empty even while
//   the user is typing. Only the input's *length* is mirrored into
//   React state for the Save-button enable gate.
//
//   On Save the parent receives the plaintext via a one-shot callback;
//   immediately afterwards we clear the ref-held value via direct DOM
//   write and reset the length counter — the plaintext never lives in
//   any React closure or component state.
//
//   Generate (optional, `onGenerate`) — for secrets WE mint and hand to
//   the other side (InPost webhook signing). The server creates and
//   stores the value and returns it exactly once; it is shown in a
//   read-only reveal panel with a Copy button until the admin clicks
//   Done. This is the one place a plaintext deliberately sits in React
//   state, because showing it is the point; it is dropped on Done, on
//   unmount, and never re-fetchable (the BE only ever sends the mask).

import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Pencil, X, Save, Trash2, Sparkles, Copy, Check } from "lucide-react";
import { useTranslation } from "../../../i18n";
import type { MaskedSecret } from "./types";

interface Props {
    label: string;
    hint?: string;
    secret: MaskedSecret;
    onSave: (plaintext: string) => Promise<void>;
    onClear: () => Promise<void>;
    disabled?: boolean;
    // HMAC secrets we mint ourselves demand 16+ chars (the default);
    // third-party credentials (e.g. APC account passwords) are whatever
    // the partner issued — pass 1 to accept any non-empty value.
    minLength?: number;
    // Mint server-side; resolves with the plaintext to reveal once. When
    // absent there is no Generate button (third-party-issued secrets).
    onGenerate?: () => Promise<string>;
}

const DEFAULT_MIN_LENGTH = 16;

const relativeTime = (iso: string | null | undefined): string => {
    if (!iso) return "";
    try {
        const ms = Date.now() - new Date(iso).getTime();
        if (ms < 0) return new Date(iso).toLocaleString();
        const s = Math.floor(ms / 1000);
        if (s < 60) return "just now";
        const m = Math.floor(s / 60);
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        if (d < 30) return `${d}d ago`;
        return new Date(iso).toLocaleDateString();
    } catch {
        return iso ?? "";
    }
};

const SecretField = ({ label, hint, secret, onSave, onClear, disabled, minLength = DEFAULT_MIN_LENGTH, onGenerate }: Props) => {
    const { t } = useTranslation();

    const [editing, setEditing] = useState<boolean>(!secret.set);
    const [draftLength, setDraftLength] = useState<number>(0);
    const [reveal, setReveal] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmingClear, setConfirmingClear] = useState<boolean>(false);
    // One-time reveal of a freshly generated secret (see header comment).
    const [generated, setGenerated] = useState<string | null>(null);
    const [copied, setCopied] = useState<boolean>(false);

    const inputRef = useRef<HTMLInputElement | null>(null);

    // Drop the plaintext with the component.
    useEffect(() => () => setGenerated(null), []);

    const handleGenerate = async () => {
        if (!onGenerate) return;
        setSaving(true);
        setError(null);
        try {
            const plaintext = await onGenerate();
            clearLiveInput();
            setConfirmingClear(false);
            setEditing(false);
            setCopied(false);
            setGenerated(plaintext);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const handleCopy = async () => {
        if (!generated) return;
        try {
            await navigator.clipboard.writeText(generated);
            setCopied(true);
        } catch {
            // Clipboard blocked (http origin / permissions): the value is
            // selectable in the box, so leave it to a manual copy.
            setCopied(false);
        }
    };

    const handleGeneratedDone = () => {
        setGenerated(null);
        setCopied(false);
    };

    const generateButton = onGenerate ? (
        <button
            type="button"
            className="btn btn-sm"
            onClick={handleGenerate}
            disabled={saving || disabled}
            title={t("integrations_secret_generate_title")}
        >
            <Sparkles size={14} /> {t("integrations_secret_generate")}
        </button>
    ) : null;

    const canSave = draftLength >= minLength && !saving && !disabled;

    const clearLiveInput = () => {
        // Direct DOM write — bypasses React state so the plaintext
        // never crosses a React closure.
        if (inputRef.current) inputRef.current.value = "";
        setDraftLength(0);
        setReveal(false);
    };

    const handleSave = async () => {
        const value = inputRef.current?.value ?? "";
        if (value.length < minLength) {
            setError(t("integrations_secret_min_length"));
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave(value);
            clearLiveInput();
            setEditing(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        clearLiveInput();
        setError(null);
        if (secret.set) setEditing(false);
    };

    const handleClearConfirm = async () => {
        setSaving(true);
        setError(null);
        try {
            await onClear();
            clearLiveInput();
            setEditing(true);
            setConfirmingClear(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="admin-integrations-field">
            <label className="admin-integrations-field-label">{label}</label>
            {hint && <p className="admin-integrations-field-hint">{hint}</p>}

            {generated && (
                <div className="admin-integrations-secret-generated" role="status">
                    <p className="admin-integrations-secret-generated-title">{t("integrations_secret_generated_title")}</p>
                    <div className="admin-integrations-secret-input-row">
                        <input
                            className="admin-integrations-input"
                            readOnly
                            value={generated}
                            onFocus={(e) => e.target.select()}
                            spellCheck={false}
                            autoComplete="off"
                            data-1p-ignore
                        />
                        <button type="button" className="btn btn-sm btn-primary" onClick={handleCopy}>
                            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? t("integrations_secret_copied") : t("integrations_secret_copy")}
                        </button>
                    </div>
                    <p className="admin-integrations-field-hint">{t("integrations_secret_generated_hint")}</p>
                    <div className="admin-integrations-secret-actions">
                        <button type="button" className="btn btn-sm" onClick={handleGeneratedDone}>
                            {t("integrations_secret_generated_done")}
                        </button>
                    </div>
                </div>
            )}

            {!editing && secret.set ? (
                <div className="admin-integrations-secret-display">
                    <span className="admin-integrations-secret-summary">
                        ••••••••&nbsp;&nbsp;
                        {t("integrations_secret_summary", { fingerprint: secret.fingerprint ?? "????" })}
                        {secret.rotatedAt && (
                            <>
                                &nbsp;·&nbsp;
                                <span className="admin-integrations-secret-rotated">
                                    {t("integrations_secret_rotated_relative", { when: relativeTime(secret.rotatedAt) })}
                                </span>
                            </>
                        )}
                    </span>
                    <div className="admin-integrations-secret-actions">
                        {generateButton}
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setEditing(true)}
                            disabled={disabled}
                        >
                            <Pencil size={14} /> {t("integrations_secret_rotate")}
                        </button>
                        {!confirmingClear ? (
                            <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => setConfirmingClear(true)}
                                disabled={disabled}
                            >
                                <Trash2 size={14} /> {t("integrations_secret_clear")}
                            </button>
                        ) : (
                            <>
                                <span className="admin-integrations-secret-confirm-text">
                                    {t("integrations_secret_clear_confirm")}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={handleClearConfirm}
                                    disabled={saving}
                                >
                                    {t("integrations_secret_clear")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() => setConfirmingClear(false)}
                                    disabled={saving}
                                >
                                    {t("integrations_secret_clear_cancel")}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <div className="admin-integrations-secret-edit">
                    {!secret.set && (
                        <span className="admin-integrations-secret-unset">
                            {t("integrations_secret_unset")}
                        </span>
                    )}
                    {/* <form> wrap + visually-hidden username input together
                        silence Chrome's DOM advisories ("password field
                        not in form" + "password forms should have a
                        username field"). The advisories matter because
                        they dump the input element to the console —
                        including any `value` attribute set on it. Combined
                        with the uncontrolled input below (React never
                        writes `value`), the attribute string is always
                        empty so even if the advisory ever fires there's
                        nothing to leak. */}
                    <form
                        className="admin-integrations-secret-input-row"
                        autoComplete="off"
                        onSubmit={(e) => { e.preventDefault(); handleSave(); }}
                    >
                        <input
                            type="text"
                            name="username"
                            value={`${label}-secret`}
                            autoComplete="username"
                            readOnly
                            aria-hidden="true"
                            tabIndex={-1}
                            style={{
                                position: "absolute",
                                width: 1,
                                height: 1,
                                padding: 0,
                                margin: -1,
                                overflow: "hidden",
                                clipPath: "inset(50%)",
                                whiteSpace: "nowrap",
                                border: 0,
                            }}
                            onChange={() => { /* satisfies React's controlled-input warning without leaking anything */ }}
                        />
                        <input
                            ref={inputRef}
                            type={reveal ? "text" : "password"}
                            className="admin-integrations-input"
                            // Uncontrolled — no `value` prop. React never
                            // writes the value attribute, so Chrome can't
                            // dump the typed value.
                            defaultValue=""
                            onInput={(e) => setDraftLength(e.currentTarget.value.length)}
                            placeholder={t("integrations_secret_placeholder")}
                            autoComplete="new-password"
                            spellCheck={false}
                            data-1p-ignore
                            disabled={saving || disabled}
                        />
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setReveal(r => !r)}
                            disabled={saving || disabled}
                            aria-label={reveal ? t("integrations_secret_hide") : t("integrations_secret_show")}
                        >
                            {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                            type="submit"
                            className="btn btn-sm btn-primary"
                            disabled={!canSave}
                        >
                            <Save size={14} /> {secret.set ? t("integrations_secret_rotate") : t("integrations_secret_set")}
                        </button>
                        {secret.set && (
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={handleCancel}
                                disabled={saving || disabled}
                                aria-label={t("cancel")}
                            >
                                <X size={14} />
                            </button>
                        )}
                    </form>
                    {generateButton && (
                        <div className="admin-integrations-secret-actions">
                            <span className="admin-integrations-secret-or">{t("integrations_secret_or")}</span>
                            {generateButton}
                        </div>
                    )}
                    {error && <p className="admin-integrations-field-error">{error}</p>}
                </div>
            )}
        </div>
    );
};

export default SecretField;
